// ──────────────────────────────────────────────────────────────────────
// Fretless Design Tokens
// Single source of truth. Import from here — don't hard-code hex/px.
//
//   import { tokens } from "@/design-system/tokens";
//   style={{ background: tokens.color.cardBg, padding: tokens.space.md }}
//
// Mirror in CSS variables: ./tokens.css
// ──────────────────────────────────────────────────────────────────────

// Colors resolve to CSS variables (defined in tokens.css) so a single
// <html data-theme="light|dark"> switch reflows every inline style that
// reads tokens.color.* — no per-component work. The raw hex lives in
// tokens.css under :root (dark) and [data-theme="light"]. Inline comments
// keep the dark-theme reference values.
export const color = {
  // Surfaces — warm-neutral near-blacks (dark), warm paper (light).
  appBg:       "var(--color-app-bg)",       // #0a0a0b — page background
  cardBg:      "var(--color-card-bg)",      // #0f0f10 — primary card surface
  cardBg2:     "var(--color-card-bg-2)",    // #0c0c0d — nested / second-tier card
  cardBorder:  "var(--color-card-border)",  // #27272a — card outer border
  innerBg:     "var(--color-inner-bg)",     // #161618 — inset surface inside a card
  innerBorder: "var(--color-inner-border)", // #262629 — inset surface border

  // Lines
  divider:     "var(--color-divider)",      // hairlines
  dividerHi:   "var(--color-divider-hi)",   // emphasized hairline (rare)

  // Text — warm-neutral, never pure white.
  text:        "var(--color-text)",         // #f5f3ee — primary text, headings
  textDim:     "var(--color-text-dim)",     // #a3a19a — section labels, secondary
  textMuted:   "var(--color-text-muted)",   // #6b6963 — empty states, captions

  // Brand & semantic — share the same desaturated mid-tone family.
  orange:      "var(--color-orange)",       // #f5891f — BRAND. primary actions
  orangeSoft:  "var(--color-orange-soft)",
  orangeLine:  "var(--color-orange-line)",

  amber:       "var(--color-amber)",        // #fbbf24 — call times, leave-by
  amberSoft:   "var(--color-amber-soft)",

  green:       "var(--color-green)",        // #22c197 — money / positive
  greenSoft:   "var(--color-green-soft)",
  red:         "var(--color-red)",          // #ef4444 — urgent / destructive
  redSoft:     "var(--color-red-soft)",
  purple:      "var(--color-purple)",       // #7a5af0 — collaborator avatar accent

  // On-color text (drawn on top of a brand fill).
  onOrange:    "var(--color-on-orange)",
  onAmber:     "var(--color-on-amber)",
  onRed:       "var(--color-on-red)",

  // Controls — segmented toggles, switches.
  segBg:       "var(--color-seg-bg)",      // toggle track
  segActive:   "var(--color-seg-active)",  // active segment
  toggleOff:   "var(--color-toggle-off)",  // switch off-state track
};

// Fonts — three families, each with one job.
export const font = {
  display:  "'Fraunces', Georgia, serif",                   // HERO ONLY. artist names, title cards.
  body:     "'Geist', -apple-system, system-ui, sans-serif",// everything else
  mono:     "'Geist Mono', ui-monospace, monospace",        // kickers, captions, numeric data, code
  wordmark: "'Mulish', -apple-system, system-ui, sans-serif",// BRAND LOGOTYPE ONLY — the "Fretless" wordmark
};

// Type scale — paired (size + weight + tracking + line-height) so calls site can do `...type.kicker`.
export const type = {
  hero: {
    fontFamily: font.display,
    fontWeight: 600,
    fontSize: "clamp(36px, 5.2vw, 64px)",
    letterSpacing: "-0.03em",
    lineHeight: 1.02,
  },
  heroSm: {                            // Page-intro hero — smaller sibling of type.hero
    fontFamily: font.display,          // Fraunces
    fontWeight: 600,
    fontSize: "clamp(28px, 3.6vw, 40px)",
    letterSpacing: "-0.03em",
    lineHeight: 1.05,
  },
  statBig: {                          // big numeric display (date, time, etc.)
    fontFamily: font.body,
    fontWeight: 600,
    fontSize: 38,
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  statMid: {                          // mid numeric (leave-by, fee)
    fontFamily: font.body,
    fontWeight: 700,
    fontSize: 32,
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  titleMd: {                          // venue, list item names
    fontFamily: font.body,
    fontWeight: 600,
    fontSize: 18,
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
  },
  titleSm: {                          // dress code, list secondary headings
    fontFamily: font.body,
    fontWeight: 600,
    fontSize: 17,
    letterSpacing: "-0.01em",
    lineHeight: 1.1,
  },
  body: {                             // paragraphs, notes
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: 14.5,
    lineHeight: 1.55,
  },
  bodySm: {                           // secondary text, addresses
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: 13,
    lineHeight: 1.4,
  },
  button: {                           // button labels
    fontFamily: font.body,
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: 0,
  },
  navLink: {                          // top-nav / sidebar link labels — sentence case
    fontFamily: font.body,
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: 0,
    lineHeight: 1,
  },
  wordmark: {                         // brand wordmark ("Fretless") — Mulish ExtraBold 800
    fontFamily: font.wordmark,
    fontWeight: 800,
    fontSize: "clamp(18px, 1.4vw, 20px)",
    letterSpacing: "-0.025em",
    lineHeight: 1,
  },
  kicker: {                           // SECTION LABELS — mono caps. The system's signature element.
    fontFamily: font.mono,
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  kickerSm: {                         // smaller kicker for dense areas
    fontFamily: font.mono,
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  kickerLg: {                         // LOUD kicker — widget titles. Pair with color.text.
    fontFamily: font.mono,
    fontWeight: 600,
    fontSize: 16,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  listPrimary: {                      // Two-line list rows — top line (artist, person, gig title).
    fontFamily: font.body,
    fontWeight: 600,
    fontSize: 18,
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
  },
  listSecondary: {                    // Two-line list rows — second line (venue, role, date).
    fontFamily: font.body,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.35,
  },
  monoCaption: {                      // mono supporting copy (timings, counts)
    fontFamily: font.mono,
    fontWeight: 500,
    fontSize: 13,
    letterSpacing: "0.04em",
  },
  monoCaptionSm: {                    // tiny mono
    fontFamily: font.mono,
    fontWeight: 500,
    fontSize: 11,
    letterSpacing: "0.04em",
  },
};

// Spacing — 4px base, named not numbered so intent stays clear at call sites.
export const space = {
  xxs: 4,
  xs:  6,
  sm:  8,
  md:  12,
  lg:  16,
  xl:  22,
  xxl: 28,
  xxxl: 40,
};

// Radii — softer than typical Tailwind. Cards 16-20, controls 8-11.
export const radius = {
  sm:   6,    // tiny chips
  md:   8,    // buttons, toggles, inputs
  lg:   11,   // small surfaces (icon tiles)
  xl:   16,   // cards (mobile)
  xxl:  20,   // cards (desktop)
  pill: 999,  // pills, avatars
};

// Shadows — used sparingly. Long, soft, off-center. Theme-aware via CSS vars
// (lighter, warmer lifts in light mode — see tokens.css).
export const shadow = {
  none: "none",
  card: "var(--shadow-card)",       // mobile card lift
  cardLg: "var(--shadow-card-lg)",  // desktop card lift
  switch: "var(--shadow-switch)",   // toggle knob
};

// Borders — 1px hairlines everywhere; no thick borders.
export const border = {
  hairline:  `1px solid ${color.divider}`,
  card:      `1px solid ${color.cardBorder}`,
  inner:     `1px solid ${color.innerBorder}`,
  brand:     `1px solid ${color.orange}`,
  brandSoft: `1px solid ${color.orangeLine}`,
};

// Motion — keep it under 200ms, ease standard.
export const motion = {
  fast:   "120ms ease",
  base:   "180ms ease",
  slow:   "240ms ease",
};

// Breakpoints — single dispatch point: desktop variant kicks in at 900px.
export const breakpoint = {
  desktop: 900,
};

// Z — flat by default.
export const z = {
  base:    1,
  raised:  10,
  overlay: 100,
  modal:   1000,
  toast:   2000,
};

// Avatar gradients — pulled from people/collaborator system. Index by position.
export const avatarGradients = [
  { from: "#f5891f", to: "#c14a09" }, // orange
  { from: "#7a5af0", to: "#4b2dc7" }, // purple
  { from: "#22c197", to: "#0e7e60" }, // green
];

// Default export bundles everything for `import { tokens } from "..."` ergonomics.
export const tokens = {
  color, font, type, space, radius, shadow, border, motion, breakpoint, z, avatarGradients,
};

export default tokens;
